import { getQuotas } from "./data/Streams";

import type { GetEncodingPlanResponse } from "../api/EncoderAPI.types";

export interface LiveQuotaSummary {
  neededSeconds: number;
  availableBeforeSeconds: number;
  isEnough: boolean;
}

export interface LiveQuotaSuggestion {
  kind: "quality" | "duration";
  quality?: string;
  durationMinutes?: number;
  neededSeconds: number;
}

export interface LiveQuotaArgs {
  durationSeconds: number;
  quality: string;
  audioOnly: boolean;
  encodingPlan: GetEncodingPlanResponse;
  liveEncodingPlan: string;
}

export const LIVE_DURATION_MIN_MINUTES = 5;
export const LIVE_DURATION_MAX_MINUTES = 1440;
export const LIVE_EXTEND_SECONDS = 1800;

const STANDARD_QUALITIES = ["360", "720", "1080"];
const EXTENDED_QUALITIES = ["360", "576", "720", "1080", "4096"];
const DURATION_STEP_MINUTES = 5;

export function isValidLiveDuration(minutes: number): boolean {
  return (
    Number.isInteger(minutes) &&
    minutes >= LIVE_DURATION_MIN_MINUTES &&
    minutes <= LIVE_DURATION_MAX_MINUTES
  );
}

export function getLiveQualities(liveEncodingPlan: string): string[] {
  return liveEncodingPlan === "standard"
    ? STANDARD_QUALITIES
    : EXTENDED_QUALITIES;
}

function getNeededSeconds(args: LiveQuotaArgs): number {
  const {
    durationSeconds,
    quality,
    audioOnly,
    encodingPlan,
    liveEncodingPlan,
  } = args;
  const quotas = getQuotas({
    stream: {
      quality,
      duration: durationSeconds,
      type: audioOnly ? "audio" : "video",
    },
    encodingPlan,
    liveEncodingPlan,
  });
  const neededSeconds: number = quotas.current.seconds;
  return neededSeconds;
}

function getAvailableSeconds(encodingPlan: GetEncodingPlanResponse): number {
  return encodingPlan.max_encoding_sec - encodingPlan.encoding_sec;
}

export function getLiveQuotaSummary(args: LiveQuotaArgs): LiveQuotaSummary {
  const neededSeconds = getNeededSeconds(args);
  const availableBeforeSeconds = getAvailableSeconds(args.encodingPlan);
  return {
    neededSeconds,
    availableBeforeSeconds,
    isEnough: neededSeconds <= availableBeforeSeconds,
  };
}

export function getLiveQuotaSuggestions(
  args: LiveQuotaArgs,
): LiveQuotaSuggestion[] {
  const summary = getLiveQuotaSummary(args);
  if (summary.isEnough) return [];

  const suggestions: LiveQuotaSuggestion[] = [];

  if (!args.audioOnly) {
    const qualities = getLiveQualities(args.liveEncodingPlan);
    const lowerQualities = qualities
      .slice(0, qualities.indexOf(args.quality))
      .reverse();
    lowerQualities.some((quality) => {
      const neededSeconds = getNeededSeconds({ ...args, quality });
      if (neededSeconds > summary.availableBeforeSeconds) return false;
      suggestions.push({ kind: "quality", quality, neededSeconds });
      return true;
    });
  }

  // Quota consumption is linear in duration: one second costs `coefficient` seconds.
  const coefficient = summary.neededSeconds / args.durationSeconds;
  if (coefficient > 0) {
    const maxMinutes = Math.floor(
      summary.availableBeforeSeconds / coefficient / 60,
    );
    const durationMinutes =
      Math.floor(maxMinutes / DURATION_STEP_MINUTES) * DURATION_STEP_MINUTES;
    if (durationMinutes >= LIVE_DURATION_MIN_MINUTES) {
      suggestions.push({
        kind: "duration",
        durationMinutes,
        neededSeconds: durationMinutes * 60 * coefficient,
      });
    }
  }

  return suggestions;
}

export function canExtendLive(args: LiveQuotaArgs): boolean {
  return getLiveQuotaSummary({
    ...args,
    durationSeconds: args.durationSeconds + LIVE_EXTEND_SECONDS,
  }).isEnough;
}

export function formatQuotaDuration(seconds: number): string {
  const totalMinutes = Math.round(seconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes} min`;
  if (minutes === 0) return `${hours} h`;
  return `${hours} h ${minutes}`;
}
